<template>
  <t-card max-width="600">
    <v-card-title>
      <v-row class="py-4 px-4">
        <v-col>
          <span v-if="dialog.config.hasTitle">CADASTRO DE BIOMETRIA</span>
        </v-col>
        <v-spacer />
        <v-btn v-if="dialog.config.hasCloseButton" icon elevation="0">
          <v-icon size="small" @click="close">
            mdi-close
          </v-icon>
        </v-btn>
      </v-row>
    </v-card-title>
    <v-card-text>
      <p>
        Neste evento todos os portadores devem realizar o cadastro de sua biometria. Para prosseguir, por favor, preencha os dados abaixo.
      </p>
    </v-card-text>
    <v-card-text>
      <v-form ref="form" @submit.prevent="submit">
        <v-row>
          <v-col cols="12">
            <t-text-input
              v-model="ticket.name"
              label="Nome do Portador"
              hide-details="auto"
              :rules="[rules.required, rules.name]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <t-text-input
              v-model="ticket.cpf"
              label="CPF"
              hide-details="auto"
              disabled
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <t-date-input
              v-model="birth_date"
              label="Data de Nascimento"
              hide-details="auto"
              required
            />
          </v-col>
          <v-col cols="12">
            <t-email-input
              v-model="email"
              label="E-mail"
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <t-phone-input
              v-model="phone"
              label="Celular"
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <t-select
              v-model="gender"
              label="Gênero"
              required
              hide-details="auto"
              :items="[{ name: 'Masculino', id: 'M' }, { name: 'Feminino', id: 'F' }, { name: 'Outro', id: 'O' }]"
              item-title="name"
              item-value="id"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-btn
              color="error"
              block
              :loading="loading"
              :disabled="finished"
              class="py-5"
              @click="close"
            >
              Voltar
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn
              color="success"
              block
              :loading="loading"
              :disabled="finished"
              class="py-5"
              @click="submit"
            >
              Confirmar
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </t-card>
</template>

<script setup lang="ts">
import TCard from "@/components/core/TCard.vue";
import TDateInput from "@/components/core/TDateInput.vue";
import TEmailInput from "@/components/core/TEmailInput.vue";
import TPhoneInput from "@/components/core/TPhoneInput.vue";
import TSelect from "@/components/core/TSelect.vue";
import TTextInput from "@/components/core/TTextInput.vue";
import BiometryService from "@/services/BiometryService";
import { useDialog } from "@/stores/dialog";
import { useEventsStore } from "@/stores/events";
import { useSnackbar } from "@/stores/snackbar";
import Event from "@/types/Event";
import NominalTicket from "@/types/NominalTicket";
import { DateTime } from "luxon";
import { computed, ref, type Ref, type ComponentPublicInstance } from "vue";
import { personName } from "@/utils/name";

defineOptions({
  name: "BiometryRegister",
});

const dialog = useDialog();
const snackbar = useSnackbar();

// Reactive state
const loading = ref(false);
const finished = ref(false);
const gender: Ref<string | null> = ref(null);
const phone: Ref<string | null> = ref(null);
const email: Ref<string | null> = ref(null);
const birth_date: Ref<string | null> = ref(null);

// Form ref with explicit typing
const form = ref<ComponentPublicInstance & { validate: () => Promise<{ valid: boolean }> }>();

// Rules
const rules = {
  required: (v: string | number) => !!v || "Campo obrigatório",
  name: (v?: string): boolean | string => !v || personName.validate(v),
};

const ticket = computed<NominalTicket>(() => {
  return dialog.config.params!.ticket as NominalTicket;
});

const event = computed<Event | null>(() => {
  return useEventsStore().selected;
});

// Methods
function close() {
  dialog.hide();
}

async function submit() {
  if (event.value === null) return;
  if (!(await form.value?.validate())?.valid) return;

  try {
    loading.value = true;

    const response = await new BiometryService().register(
      ticket.value.name!,
      ticket.value.cpf!,
      phone.value!,
      email.value!,
      gender.value!,
      DateTime.fromFormat(birth_date.value!, "dd/MM/yyyy").toFormat("yyyy-MM-dd"),
      event.value.id,
    );

    snackbar.show("Você será redirecionado para a captura da foto.", "warning");

    finished.value = true;

    setTimeout(() => window.open(response, "_self"), 3000);
  } catch {
    snackbar.show("Ocorreu um erro ao cadastrar a biometria, entre em contato com nosso suporte.", "error");
  } finally {
    loading.value = false;
  }
}
</script>
